import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Plus, Printer, Mail, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { productsAPI, companyAPI } from '../../services/api';
import { printNode } from '../../utils/print';
import OrderDocument from './OrderDocument';
import EmailModal from './EmailModal';
import { useCompany } from './useCompany';
import CustomerSelect from './CustomerSelect';
import { generateOrderPDF } from '../../utils/generateDocumentPDF';
import '../../styles/invoice-editor.css';
import { getDisplayCurrency, CURRENCY_REGISTRY, convertAmount, rateLabel } from '../../utils/currency';

const fmt = (v) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

function makeEmptyRow(n) {
  return { id: n, product_id: '', description: '', quantity: 1, unit_price: 0, amount: 0, tax_rate: 0 };
}

/**
 * Shared order editor for Estimates, Sales Orders, Purchase Orders, and Bills.
 *
 * Props:
 *   title         — "Estimate", "Sales Order", etc.
 *   api           — the API module (estimatesAPI, salesOrdersAPI, etc.)
 *   basePath      — "/estimates", "/sales-orders", etc.
 *   partyLabel    — "Customer" or "Vendor"
 *   loadParties   — async fn returning party list
 *   idField       — "customer_id" or "vendor_id"
 *   nameField     — "customer_name" or "vendor_name"
 *   dateField     — "estimate_date", "order_date", "bill_date"
 *   secondLabel   — "Expiration Date", "Expected Date", "Due Date"
 *   secondField   — "expiration_date", "expected_date", "due_date"
 *   statuses      — ["Draft","Sent","Accepted",...]
 */
export default function OrderEditor({
  title, api, basePath, partyLabel, loadParties,
  idField = 'customer_id', nameField = 'customer_name',
  dateField = 'estimate_date', secondLabel = 'Date', secondField = 'expiration_date',
  statuses = ['Draft'],
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState(CURRENCY_REGISTRY);
  const [rates, setRates] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [itemDropdown, setItemDropdown] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
  const [categories, setCategories] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const company = useCompany();
  const [showPrint, setShowPrint] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const printDocRef = useRef(null);

  const now = new Date();
  const defaultSecond = new Date(now.getTime() + 30 * 86400000);

  const [form, setForm] = useState({
    [idField]: '',
    [dateField]: now.toISOString().split('T')[0],
    [secondField]: defaultSecond.toISOString().split('T')[0],
    status: 'Draft',
    description: '',
    memo: '',
    currency: getDisplayCurrency(),
    exchange_rate: 1,
  });

  const [rows, setRows] = useState([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [shipping, setShipping] = useState(0);

  // Load data
  useEffect(() => {
    loadParties?.().then(setParties).catch(() => {});
    productsAPI.list({}).then(setProducts).catch(() => {});
    productsAPI.categories().then(setCategories).catch(() => {});
    companyAPI.currencies().then((cs) => { if (Array.isArray(cs) && cs.length) setCurrencies(cs); }).catch(() => {});
    companyAPI.exchangeRates().then((rs) => setRates(Array.isArray(rs) ? rs : [])).catch(() => {});
  }, [loadParties]);

  // Load existing for edit
  useEffect(() => {
    if (isEdit) {
      api.get(id).then(d => {
        setForm({
          [idField]: d[idField] || '',
          [dateField]: d[dateField] || '',
          [secondField]: d[secondField] || '',
          status: d.status || 'Draft',
          description: d.description || '',
          memo: d.memo || '',
          currency: d.currency || 'USD',
          exchange_rate: d.exchange_rate || 1,
        });
        setDiscount(parseFloat(d.discount_amount) || 0);
        setTax(parseFloat(d.tax_amount) || 0);
        setShipping(parseFloat(d.shipping_amount) || 0);
        if (d.items?.length) {
          setRows(d.items.map((item, i) => ({
            id: i + 1,
            product_id: item.product_id || '',
            description: item.description || item.product_name || '',
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            amount: parseFloat(item.amount) || 0,
            tax_rate: parseFloat(item.tax_rate) || 0,
          })));
        }
        const party = parties.find(p => p.id === parseInt(d[idField]));
        if (party) setSelectedParty(party);
      }).catch(e => toast.error(e.message));
    }
  }, [id, isEdit]);

  const selectParty = useCallback((partyId) => {
    const party = parties.find(p => p.id === parseInt(partyId));
    setSelectedParty(party || null);
    setForm(f => ({ ...f, [idField]: partyId }));
    if (party) {
      setForm(f => ({
        ...f,
        [idField]: partyId,
        currency: party.currency || f.currency,
      }));
    }
  }, [parties, idField]);

  const subtotal = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
  const taxAmount = useMemo(() => {
    const lineTax = rows.reduce((s, r) => s + ((r.amount || 0) * (r.tax_rate || 0) / 100), 0);
    return lineTax + tax;
  }, [rows, tax]);
  const total = useMemo(() => subtotal - discount + taxAmount + shipping, [subtotal, discount, taxAmount, shipping]);

  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const docCurrency = (form.currency && /^[A-Z]{3}$/.test(String(form.currency))) ? form.currency : (baseCurrency || 'USD');
  const fxRate = parseFloat(form.exchange_rate);
  const effectiveRate = isNaN(fxRate) || fxRate <= 0 ? 1 : fxRate;

  // Auto-fill the exchange rate whenever the document currency is not the
  // company's base currency, using the latest configured FX rate (1 doc = rate base).
  useEffect(() => {
    if (docCurrency === baseCurrency) {
      if (form.exchange_rate && parseFloat(form.exchange_rate) !== 1) {
        setForm(f => ({ ...f, exchange_rate: 1 }));
      }
      return;
    }
    const direct = rates.find(r => r.from_currency === docCurrency && r.to_currency === baseCurrency);
    const reverse = rates.find(r => r.from_currency === baseCurrency && r.to_currency === docCurrency);
    const auto = direct ? parseFloat(direct.rate) : (reverse && parseFloat(reverse.rate) > 0) ? 1 / parseFloat(reverse.rate) : null;
    const cur = parseFloat(form.exchange_rate);
    // exchange_rate is initialized to 1 as a placeholder — treat it as unset
    // so the configured rate is applied when the user picks a foreign currency.
    if (auto != null && auto > 0 && (!form.exchange_rate || cur === 1)) {
      setForm(f => ({ ...f, exchange_rate: auto }));
    }
  }, [docCurrency, baseCurrency, rates]);

  const currencySymbol = useMemo(() => {
    const c = currencies.find(x => x.code === form.currency);
    return c ? (c.symbol || c.code) : form.currency;
  }, [currencies, form.currency]);

  const updateRow = useCallback((rowId, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
      }
      return updated;
    }));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, makeEmptyRow(Date.now())]);
  }, []);

  const removeRow = useCallback((rowId) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter(r => r.id !== rowId));
  }, []);

  const selectProduct = useCallback((rowId, product) => {
    updateRow(rowId, 'product_id', product.id);
    updateRow(rowId, 'description', product.description || product.name);
    updateRow(rowId, 'unit_price', parseFloat(product.sales_price) || 0);
    updateRow(rowId, 'tax_rate', parseFloat(product.tax_rate) || 0);
    setItemDropdown(null);
    setItemSearch('');
  }, [updateRow]);

  const handleKeyDown = useCallback((e, rowId, field, rowIdx, fieldIdx) => {
    const fields = ['description', 'quantity', 'unit_price'];
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        if (fieldIdx > 0) {
          document.querySelector(`[data-row="${rowId}"][data-field="${fields[fieldIdx - 1]}"]`)?.focus();
        } else if (rowIdx > 0) {
          document.querySelector(`[data-row="${rows[rowIdx - 1].id}"][data-field="${fields[fields.length - 1]}"]`)?.focus();
        }
      } else {
        if (fieldIdx < fields.length - 1) {
          document.querySelector(`[data-row="${rowId}"][data-field="${fields[fieldIdx + 1]}"]`)?.focus();
        } else if (rowIdx < rows.length - 1) {
          document.querySelector(`[data-row="${rows[rowIdx + 1].id}"][data-field="description"]`)?.focus();
        } else {
          addRow();
          setTimeout(() => {
            const nr = rows[rows.length - 1];
            if (nr) document.querySelector(`[data-row="${nr.id}"][data-field="description"]`)?.focus();
          }, 50);
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) {
        document.querySelector(`[data-row="${rows[rowIdx + 1].id}"][data-field="description"]`)?.focus();
      } else { addRow(); }
    } else if (e.key === 'Escape') { setItemDropdown(null); }
    else if (e.key === 'Delete' && e.shiftKey) { removeRow(rowId); }
  }, [rows, addRow, removeRow]);

  const handleSave = async (andNew = false) => {
    if (!form[idField]) { toast.error(`Please select a ${partyLabel.toLowerCase()}`); return; }
    if (!form[dateField]) { toast.error('Please enter the date'); return; }
    const validRows = rows.filter(r => r.description || r.unit_price > 0);
    if (validRows.length === 0) { toast.error('Please add at least one line item'); return; }

    setLoading(true);
    try {
      const data = {
        ...form,
        subtotal,
        discount_amount: discount,
        tax_amount: taxAmount,
        shipping_amount: shipping,
        total,
        items: validRows.map(r => ({
          product_id: r.product_id || null,
          description: r.description,
          quantity: r.quantity,
          unit_price: r.unit_price,
          tax_rate: r.tax_rate || 0,
          amount: r.amount,
        })),
      };
      if (isEdit) {
        await api.update(id, data);
        toast.success(`${title} updated`);
        if (andNew) navigate(`${basePath}/new`);
        else navigate(basePath);
      } else {
        const res = await api.create(data);
        toast.success(`${title} created`);
        if (andNew) {
          const fresh = {
            [idField]: '', [dateField]: now.toISOString().split('T')[0],
            [secondField]: defaultSecond.toISOString().split('T')[0],
            status: 'Draft', description: '', memo: '', currency: getDisplayCurrency(), exchange_rate: 1,
          };
          setForm(fresh);
          setRows([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
          setSelectedParty(null);
          setDiscount(0); setTax(0); setShipping(0);
        } else {
          navigate(basePath);
        }
      }
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const handleClear = () => {
    setForm({
      [idField]: '', [dateField]: now.toISOString().split('T')[0],
      [secondField]: defaultSecond.toISOString().split('T')[0],
      status: 'Draft', description: '', memo: '', currency: getDisplayCurrency(), exchange_rate: 1,
    });
    setRows([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
    setSelectedParty(null);
    setDiscount(0); setTax(0); setShipping(0);
  };

  const filteredProducts = useMemo(() => {
    if (!itemSearch) return products.slice(0, 20);
    const q = itemSearch.toLowerCase();
    return products.filter(p => p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)).slice(0, 20);
  }, [products, itemSearch]);

  const partyAddress = useMemo(() => {
    if (!selectedParty) return null;
    const addr = [selectedParty.address_line1, selectedParty.city && `${selectedParty.city}${selectedParty.state ? `, ${selectedParty.state}` : ''} ${selectedParty.postal_code || ''}`].filter(Boolean);
    return addr.length ? addr.join('\n') : null;
  }, [selectedParty]);

  return (
    <div className="inv-editor">
      {/* TOOLBAR */}
      <div className="inv-toolbar">
        <div className="inv-toolbar-group">
          <label>{partyLabel}</label>
          <CustomerSelect
            value={form[idField]}
            onChange={(p) => selectParty(p.id)}
            type={partyLabel === 'Vendor' ? 'vendor' : 'customer'}
          />
        </div>
        <div className="inv-toolbar-sep" />
        <div className="inv-toolbar-group">
          <label>Status</label>
          <select className="inv-toolbar-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="inv-toolbar-right">
          {isEdit && (
            <>
              <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowPrint(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Printer size={13} /> Print
              </button>
              <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowEmail(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Mail size={13} /> Email
              </button>
            </>
          )}
          <span className="inv-key-hint">TAB: next &nbsp; ENTER: next row &nbsp; SHIFT+DEL: remove</span>
        </div>
      </div>

      <div className="inv-body">
        {/* HEADER */}
        <div className="inv-header">
          <div className="inv-header-left">
            <div className="inv-title">{title}</div>
            {selectedParty ? (
              <div className="inv-billto">
                <div className="inv-billto-label">{partyLabel}</div>
                <div className="inv-billto-content">
                  <strong>{selectedParty.name}</strong><br />
                  {selectedParty.email && <>{selectedParty.email}<br /></>}
                  {selectedParty.phone && <>{selectedParty.phone}<br /></>}
                  {partyAddress && <>{partyAddress.split('\n').map((l, i) => <React.Fragment key={i}>{l}<br /></React.Fragment>)}</>}
                </div>
              </div>
            ) : (
              <div className="inv-billto">
                <div className="inv-billto-label">{partyLabel}</div>
                <div className="inv-billto-content" />
              </div>
            )}
          </div>
          <div className="inv-header-right">
            <div className="inv-field-row">
              <span className="inv-field-label">{dateField.replace(/_/g, ' ').replace('date', 'Date')}</span>
              <input className="inv-field-input" type="date" value={form[dateField]} onChange={e => setForm(f => ({ ...f, [dateField]: e.target.value }))} />
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">{secondLabel}</span>
              <input className="inv-field-input" type="date" value={form[secondField]} onChange={e => setForm(f => ({ ...f, [secondField]: e.target.value }))} />
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">Currency</span>
              <select className="inv-field-input" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value, exchange_rate: f.exchange_rate || '' }))}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            {docCurrency !== baseCurrency && (
              <div className="inv-field-row">
                <span className="inv-field-label" title="Units of the base currency per 1 document currency">
                  Exchange Rate
                </span>
                <input className="inv-field-input" type="number" step="0.0001" min="0" value={form.exchange_rate}
                  onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} />
              </div>
            )}
            {docCurrency !== baseCurrency && (
              <div className="inv-field-row" style={{ justifyContent: 'flex-end' }}>
                <span className="inv-field-label fx-hint">{rateLabel(form.currency, effectiveRate, baseCurrency)}</span>
              </div>
            )}
          </div>
        </div>

        {/* LINE ITEMS GRID */}
        <div className="inv-grid-wrap" style={{ position: 'relative' }}>
          <table className="inv-grid">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th className="col-item">Item</th>
                <th className="col-desc">Description</th>
                <th className="col-qty text-right">Qty</th>
                <th className="col-rate text-right">Rate</th>
                <th className="col-amount text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={row.id} className={selectedRow === row.id ? 'selected' : ''} onClick={() => setSelectedRow(row.id)}>
                  <td className="inv-row-num">{rowIdx + 1}</td>
                  <td>
                    <div className="inv-cell" style={{ position: 'relative' }}>
                      <input
                        value={products.find(p => p.id === parseInt(row.product_id))?.name || ''}
                        placeholder="Type or select..."
                        onFocus={(e) => {
                          setSelectedRow(row.id);
                          const rect = e.target.getBoundingClientRect();
                          const wrapRect = e.target.closest('.inv-grid-wrap').getBoundingClientRect();
                          setItemDropdown({ rowId: row.id, x: rect.left - wrapRect.left, y: rect.bottom - wrapRect.top });
                          setItemSearch('');
                        }}
                        onChange={(e) => {
                          setItemSearch(e.target.value);
                          if (!itemDropdown) {
                            const rect = e.target.getBoundingClientRect();
                            const wrapRect = e.target.closest('.inv-grid-wrap').getBoundingClientRect();
                            setItemDropdown({ rowId: row.id, x: rect.left - wrapRect.left, y: rect.bottom - wrapRect.top });
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'item', rowIdx, 0)}
                        data-row={row.id} data-field="item"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input value={row.description} onChange={e => updateRow(row.id, 'description', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'description', rowIdx, 0)}
                        data-row={row.id} data-field="description" />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input type="number" min="0" step="1" className="text-right" value={row.quantity}
                        onChange={e => updateRow(row.id, 'quantity', parseFloat(e.target.value) || 0)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'quantity', rowIdx, 1)}
                        data-row={row.id} data-field="quantity" />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input type="number" min="0" step="0.01" className="text-right" value={row.unit_price}
                        onChange={e => updateRow(row.id, 'unit_price', parseFloat(e.target.value) || 0)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'unit_price', rowIdx, 2)}
                        data-row={row.id} data-field="unit_price" />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <div className="cell-amount">{currencySymbol} {fmt(row.amount)}</div>
                    </div>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <button type="button" onClick={addRow} style={{ background: 'none', border: '1px dashed #d1d5db', padding: '3px 12px', fontSize: 11, color: '#64748b', cursor: 'pointer', borderRadius: 2 }}>
                    + Add Line
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          {itemDropdown && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setItemDropdown(null)} />
              <div className="inv-item-dropdown" style={{ position: 'absolute', left: itemDropdown.x, top: itemDropdown.y + 2, minWidth: 320 }}>
                {filteredProducts.length === 0 && !itemSearch && <div style={{ padding: '8px 10px', color: '#9ca3af', fontSize: 11 }}>No items found</div>}
                {filteredProducts.map(p => (
                  <div key={p.id} className="inv-item-option" onClick={() => selectProduct(itemDropdown.rowId, p)}>
                    <div>
                      <span className="inv-item-option-name">{p.name}</span>
                      {p.sku && <span className="inv-item-option-sku">{p.sku}</span>}
                    </div>
                    <span className="inv-item-option-price">{currencySymbol}{fmt(p.sales_price)}</span>
                  </div>
                ))}
                {itemSearch && (
                  <div
                    className="inv-item-option"
                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}
                    onClick={() => {
                      setNewProduct({ name: itemSearch, sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
                      setItemDropdown(null);
                      setItemSearch('');
                      setShowAddProduct(true);
                    }}
                  >
                    <Plus size={14} style={{ marginRight: 6 }} /> Add new product "{itemSearch}"
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* BOTTOM */}
        <div className="inv-bottom">
          <div className="inv-bottom-left">
            <div className="inv-message-group">
              <label>Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description..." />
            </div>
            <div className="inv-message-group">
              <label>Memo</label>
              <input value={form.memo} onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} placeholder="Internal memo..." />
            </div>
          </div>
          <div className="inv-bottom-right">
            <div className="inv-totals">
              <div className="inv-totals-row">
                <span className="label">Subtotal</span>
                <span className="value">{currencySymbol} {fmt(subtotal)}</span>
              </div>
              <div className="inv-totals-row">
                <span className="label">Discount</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row">
                <span className="label">Tax</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={tax} onChange={e => setTax(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row total-row">
                <span className="label">Total</span>
                <span className="value">{currencySymbol} {fmt(total)}</span>
              </div>
              {docCurrency !== baseCurrency && total > 0 && (
                <div className="inv-totals-row inv-fx-total">
                  <span className="label">≈ in {baseCurrency}</span>
                  <span className="value">{fmt(convertAmount(total, effectiveRate))} {baseCurrency}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="inv-actions">
          <button type="button" className="inv-btn inv-btn-danger" onClick={handleClear}>Clear</button>
          <button type="button" className="inv-btn inv-btn-secondary" onClick={() => navigate(basePath)} disabled={loading}>Cancel</button>
          <button type="button" className="inv-btn inv-btn-secondary" onClick={() => handleSave(false)} disabled={loading}>
            {loading ? 'Saving...' : 'Save & Close'}
          </button>
          <button type="button" className="inv-btn inv-btn-primary" onClick={() => handleSave(true)} disabled={loading}>
            {loading ? 'Saving...' : 'Save & New'}
          </button>
        </div>
      </div>

      {/* Add New Product Modal */}
      {showAddProduct && createPortal(
        <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Product</h3>
              <button type="button" className="btn-ghost" onClick={() => setShowAddProduct(false)}>✕</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newProduct.name.trim()) { toast.error('Product name is required'); return; }
              setSavingProduct(true);
              try {
                const res = await productsAPI.create({
                  name: newProduct.name.trim(),
                  sku: newProduct.sku.trim() || undefined,
                  description: newProduct.description.trim() || undefined,
                  sales_price: parseFloat(newProduct.sales_price) || 0,
                  purchase_cost: parseFloat(newProduct.purchase_cost) || 0,
                  tax_rate: parseFloat(newProduct.tax_rate) || 0,
                  product_type: newProduct.product_type || 'NonInventory',
                  is_tracked: newProduct.product_type === 'Inventory',
                  category_id: newProduct.category_id ? parseInt(newProduct.category_id) : null,
                });
                // Refresh product list and select the new product
                const updated = await productsAPI.list({});
                setProducts(updated);
                const created = updated.find(p => p.id === res.id);
                if (created && itemDropdown) {
                  selectProduct(itemDropdown.rowId, created);
                }
                toast.success('Product added');
                setShowAddProduct(false);
              } catch (err) { toast.error(err.message); }
              setSavingProduct(false);
            }}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name <span className="required">*</span></label>
                    <input className="form-control" autoFocus value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>SKU</label>
                    <input className="form-control" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-control" value={newProduct.product_type} onChange={e => setNewProduct({ ...newProduct, product_type: e.target.value })}>
                      <option value="NonInventory">Non-Inventory</option>
                      <option value="Inventory">Inventory</option>
                      <option value="Service">Service</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-control" value={newProduct.category_id} onChange={e => setNewProduct({ ...newProduct, category_id: e.target.value })}>
                      <option value="">None</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea className="form-control" rows={2} value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Sales Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.sales_price} onChange={e => setNewProduct({ ...newProduct, sales_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Purchase Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.purchase_cost} onChange={e => setNewProduct({ ...newProduct, purchase_cost: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Tax %</label>
                    <input className="form-control" type="number" step="0.01" min="0" value={newProduct.tax_rate} onChange={e => setNewProduct({ ...newProduct, tax_rate: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddProduct(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingProduct}>{savingProduct ? 'Adding...' : 'Add & Select'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Email Modal */}
      {showEmail && (
        <EmailModal
          show={showEmail}
          onClose={() => setShowEmail(false)}
          type={title?.toLowerCase().replace(/\s+/g, '_') || 'generic'}
          refId={id}
          number={form[idField] || ''}
          recipientEmail={selectedParty?.email || ''}
          recipientName={selectedParty?.name || ''}
        />
      )}

      {/* Print Preview Modal */}
      {showPrint && (
        <div className="modal-overlay" onClick={() => setShowPrint(false)} style={{ zIndex: 1000 }}>
          <div className="modal" style={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Print {title}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const el = printDocRef.current;
                    if (el) printNode(el, `${title} - ${form[nameField] || form[idField] || ''}`);
                  }}
                >
                  <Printer size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Print
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={async () => { try { await generateOrderPDF(title, {
                    number: form[idField] || '',
                    date: form[dateField] || '',
                    secondLabel,
                    second: form[secondField] || '',
                    status: form.status,
                    currency: form.currency,
                    exchange_rate: form.exchange_rate,
                    partyLabel,
                    party_name: selectedParty?.name,
                    party_address: partyAddress,
                    party_email: selectedParty?.email,
                    party_phone: selectedParty?.phone,
                    items: rows.filter(r => r.description || r.unit_price > 0).map(r => ({
                      description: r.description,
                      quantity: r.quantity,
                      unit_price: r.unit_price,
                      tax_rate: r.tax_rate,
                      tax_amount: (r.unit_price * r.quantity * (r.tax_rate || 0)) / 100,
                      amount: r.amount,
                    })),
                    subtotal,
                    tax_amount: taxAmount,
                    discount_amount: discount,
                    total,
                    notes: form.description,
                  }, company); } catch(e) { toast.error('PDF failed: ' + e.message); } }}
                >
                  <Download size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> PDF
                </button>
                <button type="button" className="btn-ghost" onClick={() => setShowPrint(false)}>✕</button>
              </div>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div ref={printDocRef} style={{ padding: 20 }}>
                <OrderDocument
                  title={title}
                  partyLabel={partyLabel}
                  docType={title === 'Estimate' ? 'estimate' : title === 'Sales Order' ? 'sales-order' : title === 'Purchase Order' ? 'purchase-order' : title === 'Bill' ? 'purchase-invoice' : 'estimate'}
                  data={{
                    number: form[`${title.toLowerCase().replace(/\s/g, '_').replace(/order$/, '_number').replace(/bill$/, '_number')}`] || form.po_number || form.estimate_number || form.order_number || form.bill_number || (isEdit ? id : 'New'),
                    dateLabel: dateField.replace(/_/g, ' ').replace(/date/i, 'Date'),
                    date: form[dateField] || '—',
                    secondLabel,
                    second: form[secondField] || null,
                    status: form.status,
                    currency: form.currency,
                    exchange_rate: form.exchange_rate,
                    party_name: selectedParty?.name,
                    party_email: selectedParty?.email,
                    party_phone: selectedParty?.phone,
                    party_address: selectedParty ? [selectedParty.address_line1, selectedParty.city && `${selectedParty.city}${selectedParty.state ? `, ${selectedParty.state}` : ''} ${selectedParty.postal_code || ''}`].filter(Boolean).join(', ') : null,
                    subtotal,
                    tax_amount: taxAmount,
                    total,
                    memo: form.memo,
                    items: rows.filter(r => r.description || r.unit_price > 0).map(r => ({
                      description: r.description,
                      quantity: r.quantity,
                      unit_price: r.unit_price,
                      tax_rate: r.tax_rate,
                      tax_amount: (r.unit_price * r.quantity * (r.tax_rate || 0)) / 100,
                      amount: r.amount,
                    })),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
